import { Refresh } from "@mui/icons-material";
import {
  Container,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  FormLabel,
  FormControl
} from "@mui/material";
import { globalDateTimeFormat, useToast } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";

import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useGridSelector } from "~/features/inventory/useGridSelector";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";

import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useGetBinsByFlaggedReasonQuery } from "~/redux/warehouse/bins.openApi";

interface EscalatedBins {
  viewTitle: ViewNameTranslation;
}

export const EscalatedBins: React.FC<EscalatedBins> = (props) => {
  const { viewTitle } = props;
  useNavbar({ viewTitle });
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);

  const { autostoreGridOverride: autostoreGridId, gridSelectorButton } =
    useGridSelector({ id: "grid-selector" });

  const {
    data: flaggedBins,
    error,
    isLoading,
    refetch,
    fulfilledTimeStamp
  } = useGetBinsByFlaggedReasonQuery(
    autostoreGridId
      ? {
          autostoreGridId,
          flaggedReason: "exterior maintenance"
        }
      : skipToken
  );

  if (error) {
    errorToast(getMessageFromRtkError(error));
  }

  return (
    <Container maxWidth="md" sx={{ p: 4 }}>
      <Stack direction="column" pb={1}>
        <Typography variant="h6" sx={{ py: 1 }}>
          {t("bins marked for exterior maintenance")}
        </Typography>
        <Stack direction="row" justifyContent="space-between" pb={1}>
          <FormControl>
            <FormLabel htmlFor="grid-selector">
              {`${t("autostore grid")}`}
            </FormLabel>
            {gridSelectorButton}
          </FormControl>
          <Stack direction="row" spacing={2}>
            <Stack>
              <Typography variant="subtitle1">
                {t("last updated") + ":"}
              </Typography>
              <Typography variant="body2">
                {dayjs(fulfilledTimeStamp).format("lll")}
              </Typography>
            </Stack>
            <Button
              startIcon={<Refresh />}
              variant="subtle"
              onClick={async () => {
                await refetch();
              }}
            >
              {t("refresh")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left">{t("bin id")}</TableCell>
            <TableCell align="right">{t("date escalated")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={2}>
                <Skeleton role="progressbar" variant="text" />
              </TableCell>
            </TableRow>
          )}
          {!isLoading &&
            autostoreGridId &&
            flaggedBins?.map((binData) => (
              <TableRow key={binData.autostoreBinId}>
                <TableCell align="left">{binData.autostoreBinId}</TableCell>
                <TableCell align="right">
                  {dayjs(binData.flaggedTime)
                    .tz(usersFulfillmentCenter?.timeZone ?? undefined)
                    .format(globalDateTimeFormat)}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Container>
  );
};
